'use client'

import { useState, useCallback, useImperativeHandle, forwardRef } from 'react'
import { cn } from '../../helpers/index.js'
import { DEFAULT_CHANCE_TITLE } from './types.js'
import { DEFAULT_CHANCE_METHODS } from '../LotteryShared/types.js'
import { Grid, GridItem } from '../../components/grid.js'
import { MediaDrawArea } from './MediaDrawArea.js'
import { PrizePool } from '../LotteryShared/PrizePool.js'
import { ChanceMethods } from '../LotteryShared/ChanceMethods.js'
import { WinnerModal } from '../LotteryShared/WinnerModal.js'
import { RulesModal } from '../LotteryShared/RulesModal.js'
import { MyRewardsModal } from '../LotteryShared/MyRewardsModal.js'
import { ErrorModal } from '../LotteryShared/ErrorModal.js'
import { ShareModal } from '../LotteryShared/ShareModal.js'
import { withLayout } from '../../shared/Styles.js'

import type { ProductLotteryProps, ProductLotteryHandle } from './types.js'
import type { Prize, WinnerModalConfig } from '../LotteryShared/types.js'

export const ProductLottery = forwardRef<ProductLotteryHandle, ProductLotteryProps>(
  (
    {
      prizes,
      media,
      theme,
      ctaButton,
      chanceMethods = DEFAULT_CHANCE_METHODS,
      chanceTitle = DEFAULT_CHANCE_TITLE,
      chanceSubtitle,
      chanceFooterNote,
      chanceMethodsText,
      mechanismType,
      inputConfig,
      winningInfos = [],
      prizesTitle,
      onDrawStart,
      onDrawEnd,
      onDrawError,
      winnerModalConfig,
      rulesModalConfig = {},
      myRewardsModalConfig = {},
      errorModalConfig = {},
      shareModalConfig = {},
      userData,
      loginPromptText = 'Please log in to participate',
      loginButtonText = 'Log In',
      noChancesText = 'You have no chances left',
      onLoginRequired,
      onCopyCode,
      opportunitiesText = ['Number of draws remaining today:'],
      className,
    },
    ref
  ) => {
    const { rulesData, rulesText = 'Rules' } = rulesModalConfig
    const {
      rewardsData,
      myRewardsText = 'My Rewards',
      codeText = 'CODE:',
      copyText = 'COPY',
      copiedText = 'COPIED',
      prizeText = 'Prize time:',
    } = myRewardsModalConfig
    const completedText = chanceMethodsText?.completedText
    const usedText = chanceMethodsText?.usedText
    const isLoggedIn = userData?.isLoggedIn ?? false
    const availableChances = userData?.availableChances || 0
    const hasRightPanel = chanceMethods.length > 0 || mechanismType === 'input'
    const showNoChancesOverlay = isLoggedIn && availableChances <= 0

    // State
    const [isDrawing, setIsDrawing] = useState(false)
    const [winningPrize, setWinningPrize] = useState<Prize | null>(null)
    const [showWinnerModal, setShowWinnerModal] = useState(false)
    const [showRulesModal, setShowRulesModal] = useState(false)
    const [showRewardsModal, setShowRewardsModal] = useState(false)
    const [showErrorModal, setShowErrorModal] = useState(false)
    const [showNoWinModal, setShowNoWinModal] = useState(false)
    const [showShareModal, setShowShareModal] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [errorTitle, setErrorTitle] = useState<string | undefined>()
    const [errorConfirmText, setErrorConfirmText] = useState<string | undefined>()
    const [noWinTitle, setNoWinTitle] = useState<string | undefined>()
    const [noWinMessage, setNoWinMessage] = useState<string | undefined>()
    const [noWinConfirmText, setNoWinConfirmText] = useState<string | undefined>()
    const [externalWinnerConfig, setExternalWinnerConfig] = useState<Partial<WinnerModalConfig> | undefined>()

    useImperativeHandle(
      ref,
      () => ({
        showError: ({ message, title, confirmText }) => {
          setErrorMessage(message)
          setErrorTitle(title)
          setErrorConfirmText(confirmText)
          setShowErrorModal(true)
        },
        hideError: () => {
          setShowErrorModal(false)
        },
        showNoWin: config => {
          setNoWinTitle(config?.title)
          setNoWinMessage(config?.message)
          setNoWinConfirmText(config?.confirmText)
          setShowNoWinModal(true)
        },
        hideNoWin: () => {
          setShowNoWinModal(false)
        },
        showWinner: (prize, config) => {
          setWinningPrize(prize)
          setExternalWinnerConfig(config)
          setShowWinnerModal(true)
        },
        hideWinner: () => {
          setShowWinnerModal(false)
        },
        showRules: () => {
          setShowRulesModal(true)
        },
        hideRules: () => {
          setShowRulesModal(false)
        },
        showRewards: () => {
          setShowRewardsModal(true)
        },
        hideRewards: () => {
          setShowRewardsModal(false)
        },
        showShare: () => {
          setShowShareModal(true)
        },
        hideShare: () => {
          setShowShareModal(false)
        },
        hideAllModals: () => {
          setShowWinnerModal(false)
          setShowRulesModal(false)
          setShowRewardsModal(false)
          setShowErrorModal(false)
          setShowNoWinModal(false)
          setShowShareModal(false)
        },
      }),
      []
    )

    const handleDrawStart = useCallback(async () => {
      if (isDrawing) return

      if (!userData?.isLoggedIn) {
        onLoginRequired?.()
        return
      }

      if (userData.availableChances <= 0) {
        return
      }

      setIsDrawing(true)

      try {
        let prizeId: string | undefined

        if (onDrawStart) {
          prizeId = await onDrawStart()
        }

        const prize = prizes.find(p => p.prizeKey === prizeId) || prizes[0]
        setWinningPrize(prize)
        setIsDrawing(false)

        if (onDrawEnd) {
          onDrawEnd(prize)
        } else {
          setTimeout(() => {
            if (prize.prizeKey === 'try-again') {
              setShowNoWinModal(true)
            } else {
              setShowWinnerModal(true)
            }
          }, 300)
        }
      } catch (error) {
        setIsDrawing(false)
        setErrorMessage((error as Error).message || 'An error occurred')
        setShowErrorModal(true)
        onDrawError?.(error as Error)
      }
    }, [isDrawing, prizes, onDrawStart, onDrawEnd, onDrawError, userData, onLoginRequired])

    const handleDrawClick = useCallback(() => {
      if (!isLoggedIn) {
        onLoginRequired?.()
        return
      }
      void handleDrawStart()
    }, [handleDrawStart, isLoggedIn, onLoginRequired])

    const handleCloseWinnerModal = useCallback(() => {
      setShowWinnerModal(false)
    }, [])

    const handleCloseNoWinModal = useCallback(() => {
      setShowNoWinModal(false)
    }, [])

    return (
      <div className={cn('product-lottery-container', 'w-full', theme === 'dark' ? 'aiui-dark' : '', className)}>
        {/* ARIA Live Region */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {isDrawing && 'Drawing in progress...'}
          {winningPrize && !isDrawing && `Prize won: ${winningPrize.name}`}
        </div>

        {/* Main Content — Responsive Grid */}
        <Grid className={cn('product-lottery-main', 'tablet:mb-4 mb-3')}>
          {/* Left: Media Draw Area */}
          <GridItem
            span={hasRightPanel ? 7 : 12}
            className={cn(
              'media-section',
              'relative flex w-full flex-1 items-center justify-center overflow-hidden',
              'rounded-box',
              'laptop:min-h-[288px] min-h-[240px]',
              'lg-desktop:aspect-w-[964] lg-desktop:aspect-h-[480]',
              'desktop:aspect-w-[759] desktop:aspect-h-[384]',
              'laptop:aspect-w-[516] laptop:aspect-h-[288]',
              'l-tablet:col-span-12',
              'l-tablet:mb-3',
              theme === 'dark' ? 'bg-[#1E2024]' : 'bg-[#EAEAEC]'
            )}
          >
            <div
              className={cn(
                'size-full px-[16px] pt-[16px]',
                'lg-desktop:pb-[32px] lg-desktop:px-[32px]',
                'desktop:pt-[24px] desktop:px-[24px]',
                'laptop:pt-[16px] laptop:px-[16px]',
                'tablet:px-[24px]',
                'l-tablet:absolute l-tablet:top-0 l-tablet:inset-x-0 l-tablet:z-10'
              )}
            >
              {/* Top bar: chances + rules/rewards links */}
              <div className="relative z-10 mb-2 flex max-h-[24px] w-full flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-info-primary font-[700]">
                    {opportunitiesText?.[0]}
                    <span className="ml-1 text-[#FF6B35]">{availableChances}</span>
                    {opportunitiesText?.[1] || ''}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  {rulesData && (
                    <button
                      onClick={() => {
                        if (!isLoggedIn) {
                          onLoginRequired?.()
                          return
                        }
                        setShowRulesModal(true)
                      }}
                      className="cursor-pointer touch-manipulation"
                      type="button"
                    >
                      <span className="text-info-primary font-[700] underline">{rulesText}</span>
                    </button>
                  )}
                  {rewardsData && (
                    <button
                      onClick={() => {
                        if (!isLoggedIn) {
                          onLoginRequired?.()
                          return
                        }
                        setShowRewardsModal(true)
                      }}
                      className="cursor-pointer touch-manipulation whitespace-nowrap"
                      type="button"
                    >
                      <span className="text-info-primary font-[700] underline">{myRewardsText}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Media Draw Area */}
              <MediaDrawArea
                theme={theme}
                media={media}
                ctaButton={ctaButton}
                isDrawing={isDrawing}
                isDisabled={showNoChancesOverlay}
                onClick={handleDrawClick}
              />

              {showNoChancesOverlay && (
                <div
                  className={cn(
                    'rounded-box absolute inset-0 z-20 flex items-center justify-center bg-black/60 backdrop-blur-sm',
                    'px-6 text-center'
                  )}
                >
                  <div className="flex flex-col items-center gap-4">
                    <p className="lg-desktop:text-[24px] text-[18px] font-semibold text-white">{noChancesText}</p>
                  </div>
                </div>
              )}
            </div>
          </GridItem>

          {/* Right: Chance Methods */}
          {hasRightPanel && (
            <GridItem
              span={5}
              className={cn(
                'info-section',
                'rounded-box',
                'relative flex flex-col overflow-hidden',
                'lg-desktop:aspect-w-[684] lg-desktop:aspect-h-[480]',
                'desktop:aspect-w-[536] desktop:aspect-h-[384]',
                'laptop:aspect-w-[364] laptop:aspect-h-[288]',
                'l-tablet:min-h-[280px] laptop:max-h-[320px]',
                'w-full',
                'l-tablet:col-span-12',
                theme === 'dark' ? 'bg-[#1E2024]' : 'bg-[#EAEAEC]'
              )}
            >
              <ChanceMethods
                theme={theme}
                userData={userData}
                methods={chanceMethods}
                title={chanceTitle}
                subtitle={chanceSubtitle}
                footerNote={chanceFooterNote}
                onOpenShareModal={() => setShowShareModal(true)}
                chanceBadgeText={chanceMethodsText?.chanceBadgeText}
                completedText={completedText}
                usedText={usedText}
                loadingText={chanceMethodsText?.loadingText}
                mechanismType={mechanismType}
                inputConfig={inputConfig}
              />
            </GridItem>
          )}
        </Grid>

        {/* Footer: Prize Pool */}
        <div
          className={cn(
            'product-lottery-footer',
            'rounded-box',
            'flex flex-col overflow-hidden',
            theme === 'dark' ? 'bg-[#1E2024]' : 'bg-[#EAEAEC]'
          )}
        >
          <PrizePool theme={theme} prizes={prizes} title={prizesTitle} winningInfos={winningInfos} />
        </div>

        {/* Winner Modal */}
        <WinnerModal
          isOpen={showWinnerModal}
          prize={winningPrize}
          title={externalWinnerConfig?.title || winnerModalConfig?.title}
          prizeTitle={externalWinnerConfig?.prizeTitle || winnerModalConfig?.prizeTitle}
          prizeImage={externalWinnerConfig?.prizeImage || winnerModalConfig?.prizeImage}
          prizeDescription={externalWinnerConfig?.prizeDescription || winnerModalConfig?.prizeDescription}
          learnMoreUrl={externalWinnerConfig?.learnMoreUrl || winnerModalConfig?.learnMoreUrl}
          learnMoreText={externalWinnerConfig?.learnMoreText || winnerModalConfig?.learnMoreText}
          couponCode={externalWinnerConfig?.couponCode || winnerModalConfig?.couponCode}
          expiresAt={externalWinnerConfig?.expiresAt || winnerModalConfig?.expiresAt}
          couponDiscount={externalWinnerConfig?.couponDiscount || winnerModalConfig?.couponDiscount}
          couponUnit={externalWinnerConfig?.couponUnit || winnerModalConfig?.couponUnit}
          confirmText={externalWinnerConfig?.confirmText || winnerModalConfig?.confirmText}
          onClose={handleCloseWinnerModal}
          theme={theme}
        />

        {/* Rules Modal */}
        {rulesData && (
          <RulesModal
            title={rulesText}
            isOpen={showRulesModal}
            onClose={() => setShowRulesModal(false)}
            rules={rulesData}
            theme={theme}
          />
        )}

        {/* My Rewards Modal */}
        {rewardsData && (
          <MyRewardsModal
            isOpen={showRewardsModal}
            onClose={() => setShowRewardsModal(false)}
            rewards={rewardsData}
            onCopyCode={onCopyCode}
            codeText={codeText}
            copyText={copyText}
            copiedText={copiedText}
            prizeText={prizeText}
            theme={theme}
            title={myRewardsText}
          />
        )}

        {/* Error Modal */}
        <ErrorModal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title={errorTitle || errorModalConfig?.title}
          message={errorMessage || errorModalConfig?.message}
          confirmText={errorConfirmText || errorModalConfig?.confirmText}
          theme={theme}
        />

        {/* No-Win Modal */}
        <ErrorModal
          isOpen={showNoWinModal}
          onClose={handleCloseNoWinModal}
          title={noWinTitle || errorModalConfig?.title}
          message={noWinMessage || errorModalConfig?.message}
          confirmText={noWinConfirmText || errorModalConfig?.confirmText}
          theme={theme}
        />

        {/* Share Modal */}
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title={shareModalConfig?.title}
          subtitle={shareModalConfig?.subtitle}
          note={shareModalConfig?.note}
          platforms={shareModalConfig?.platforms}
          onShareSuccess={shareModalConfig?.onShareSuccess}
          theme={theme}
        />
      </div>
    )
  }
)

ProductLottery.displayName = 'ProductLottery'

export default withLayout(ProductLottery)
export { WinnerModal } from '../LotteryShared/WinnerModal.js'
export { RulesModal } from '../LotteryShared/RulesModal.js'
export { MyRewardsModal } from '../LotteryShared/MyRewardsModal.js'
export { ErrorModal } from '../LotteryShared/ErrorModal.js'
export { ShareModal } from '../LotteryShared/ShareModal.js'
export { BaseModal } from '../LotteryShared/BaseModal.js'
export type {
  ProductLotteryProps,
  ProductLotteryHandle,
  MediaConfig,
  CtaButtonConfig,
  ResponsiveMedia,
  MediaDrawAreaProps,
} from './types.js'
export type {
  Prize,
  ChanceMethod,
  ChanceMethodStatus,
  WinningInfo,
  PrizePoolProps,
  ChanceMethodsProps,
  WinnerModalProps,
  WinnerModalConfig,
  UserData,
  ShareModalConfig,
  SocialPlatform,
  SharePlatformConfig,
} from '../LotteryShared/types.js'
export type { RulesModalProps, RuleItem } from '../LotteryShared/RulesModal.js'
export type { MyRewardsModalProps, Reward, RewardStatus } from '../LotteryShared/MyRewardsModal.js'
export type { ErrorModalProps } from '../LotteryShared/ErrorModal.js'
export type { ShareModalProps } from '../LotteryShared/ShareModal.js'
export type { BaseModalProps } from '../LotteryShared/BaseModal.js'
